import React, { useState, ReactNode } from 'react';
import {
  IconButton,
  CircularProgress,
  Box,
  Typography,
  Button,
  SxProps,
  Theme,
} from '@mui/material';
import {
  Description as DocumentIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { makeStyles } from '@mui/styles';
import { useLastOrderForm } from 'hooks/useLastOrderForm';
import { OrderFormPreviewModal } from './order-form-preview-modal';

const useStyles = makeStyles((theme: any) => ({
  iconButton: {
    padding: theme.spacing(0.5),
    marginLeft: theme.spacing(0.5),
  },
}));

interface IOrderFormPreviewButtonProps {
  patientId: number;
  therapyId: number;
  customerId: number;
  disabled?: boolean;
  size?: 'small' | 'medium' | 'large';
  variant?: 'icon' | 'link';
  /** Override/merge styles for the container Box (link variant only) */
  containerSx?: SxProps<Theme>;
  /** Content to prepend inside the container Box (link variant only) */
  prependContent?: ReactNode;
}

export const OrderFormPreviewButton: React.FC<IOrderFormPreviewButtonProps> = ({
  patientId,
  therapyId,
  customerId,
  disabled = false,
  size = 'small',
  variant = 'icon',
  containerSx,
  prependContent,
}) => {
  const classes = useStyles();
  const [modalOpen, setModalOpen] = useState(false);

  const { orderForm, loading, error } = useLastOrderForm(patientId, therapyId, !disabled);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    if (!disabled && orderForm) {
      setModalOpen(true);
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      return `${month}/${day}/${year}`;
    } catch {
      return dateString;
    }
  };

  const getIcon = () => {
    if (loading) {
      return <CircularProgress size={16} />;
    }
    if (error) {
      return <ErrorIcon fontSize={size} color="error" />;
    }
    if (!orderForm) {
      return <InfoIcon fontSize={size} color="disabled" />;
    }
    return <DocumentIcon fontSize={size} color="primary" />;
  };

  if (!orderForm && variant === 'link') {
    if (loading) {
      return (
        <Box
          sx={{
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
            padding: '12px 16px',
            borderRadius: '4px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 2,
            mt: 2,
            mb: 2,
            width: 'fit-content',
          }}
        >
          <CircularProgress size={16} />
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Loading order form...
          </Typography>
        </Box>
      );
    }
    return null;
  }

  if (variant === 'link' && orderForm) {
    // Normalize containerSx to array for sx prop spreading
    let additionalStyles: any[] = [];
    if (containerSx) {
      additionalStyles = Array.isArray(containerSx) ? containerSx : [containerSx];
    }

    return (
      <>
        <Box
          sx={[
            {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
              padding: '12px 16px',
              borderRadius: '4px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 2,
              mt: 2,
              mb: 2,
              width: 'fit-content',
            },
            // Merge any custom containerSx styles
            ...additionalStyles,
          ]}
        >
          {prependContent}
          <Typography variant="body2" sx={{ color: 'text.primary' }}>
            FC #{orderForm.fillCycleNumber} - {formatDate(orderForm.completedDate)}
          </Typography>
          <Button
            startIcon={<DocumentIcon />}
            onClick={handleClick}
            disabled={disabled || loading}
            size="small"
            sx={{
              'textTransform': 'none',
              'padding': 0,
              'minWidth': 'auto',
              '&:hover': {
                backgroundColor: 'transparent',
                textDecoration: 'underline',
              },
            }}
          >
            Order form #{orderForm.orderId}
          </Button>
        </Box>

        {orderForm && (
          <OrderFormPreviewModal
            open={modalOpen}
            onClose={handleModalClose}
            documentId={orderForm.documentId}
            patientId={patientId}
            customerId={customerId}
            metadata={{
              fileName: orderForm.fileName,
              completedDate: orderForm.completedDate,
              fillCycleNumber: orderForm.fillCycleNumber,
              orderId: orderForm.orderId,
            }}
          />
        )}
      </>
    );
  }

  return (
    <>
      <IconButton
        className={classes.iconButton}
        onClick={handleClick}
        disabled={disabled || loading || !orderForm}
        size={size}
        aria-label="View last order form"
        title="View last order form"
      >
        {getIcon()}
      </IconButton>

      {orderForm && (
        <OrderFormPreviewModal
          open={modalOpen}
          onClose={handleModalClose}
          documentId={orderForm.documentId}
          patientId={patientId}
          customerId={customerId}
          metadata={{
            fileName: orderForm.fileName,
            completedDate: orderForm.completedDate,
            fillCycleNumber: orderForm.fillCycleNumber,
            orderId: orderForm.orderId,
          }}
        />
      )}
    </>
  );
};
